import { Box } from '@mui/material';
import { Container, Heading, Section, Text } from '../components/atomic';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Privacy Policy page component for RSM Collective
 *
 * Comprehensive privacy policy covering data collection, usage, and user rights
 * following current privacy regulations and best practices.
 *
 * @returns {JSX.Element} Privacy Policy page component
 */
function PrivacyPolicy() {
  return (
    <LegalPageLayout>
      <Section
        sx={{
          backgroundColor: 'var(--color-background-light)',
          py: 0,
          position: 'relative',
        }}
      >
        {/* Green Bar Header Treatment */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            py: 4,
            mb: 6,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Privacy Policy
              </Heading>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* Introduction */}
            <Box sx={{ mb: 6 }}>
              <Text sx={{ fontSize: '1.125rem', lineHeight: 1.7, mb: 3 }}>
                This Privacy Policy ("Policy") applies to EllaFin™, and RSM
                Collective ("Company") and governs data collection and usage.
                For the purposes of this Privacy Policy, unless otherwise noted,
                all references to the Company include www.rsmc.ai. The Company's
                application is a FinOps Business Intelligence Platform
                application. By using the Company application, you consent to
                the data practices described in this statement.
              </Text>
            </Box>

            {/* California Consumer Privacy Act */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                California Consumer Privacy Act and California Privacy Rights
                Act
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you are a California resident, you have the following rights
                under the California Consumer Privacy Act ("CCPA") and
                California Privacy Rights Act ("CPRA"):
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right to Know.</strong> You may request details on
                  what personal data we collect, use, and share.
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right to Delete.</strong> You can request deletion of
                  personal data, subject to certain legal exceptions.
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right to Correct.</strong> You may request corrections
                  to inaccurate personal information.
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right to Opt-Out.</strong> You can opt out of the sale
                  or sharing of personal data for advertising.
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right to Restrict Sensitive Data Use.</strong> You may
                  limit the use of sensitive personal information.
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  <strong>Right Against Retaliation.</strong> The Company will
                  not discriminate against you for exercising your rights.
                </Text>
              </Box>
            </Box>

            {/* Collection of Personal Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Collection of your Personal Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                In order to better provide you with products and services
                offered, the Company may collect personally identifiable
                information, such as your:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  First and last name
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Email address
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Phone number
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you purchase the Company's products and services, we collect
                billing and credit card information. This information is used to
                complete the purchase transaction.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We do not collect any personal information about you unless you
                voluntarily provide it to us. However, you may be required to
                provide certain personal information to us when you elect to use
                certain products or services. These may include: (a) registering
                for an account; (b) entering a sweepstakes or contest sponsored
                by us or one of our partners; (c) signing up for special offers
                from selected third parties; (d) sending us an email message;
                (e) submitting your credit card or other payment information
                when ordering and purchasing products and services. To wit, we
                will use your information for, but not limited to, communicating
                with you in relation to services and/or products you have
                requested from us. We also may gather additional personal or
                non-personal information in the future.
              </Text>
            </Box>

            {/* Use of Personal Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Use of your Personal Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company collects and uses your personal information in the
                following ways:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  to operate and deliver the services you have requested
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  to provide you with information, products, or services that
                  you request from us
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  to provide you with notices about your account
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  to carry out the Company's obligations and enforce our rights
                  arising from any contracts entered between you and us,
                  including for billing and collection
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  to notify you about changes to EllaFin™ or any products or
                  services we offer or provide through it
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  in any other way we may describe when you provide the
                  information
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  for any other purpose with your consent.
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company may also use your personally identifiable
                information to inform you of other products or services
                available from the Company and its affiliates.
              </Text>
            </Box>

            {/* Sharing Information with Third Parties */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Sharing Information with Third Parties
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company does not sell, rent, or lease its customer lists to
                third parties.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company may, from time to time, contact you on behalf of
                external business partners about a particular offering that may
                be of interest to you. In those cases, your unique personally
                identifiable information (email, name, address, phone number) is
                transferred to the third party. The Company may share data with
                trusted partners to help perform statistical analysis, send you
                email or postal mail, provide customer support, or arrange for
                deliveries. All such third parties are prohibited from using
                your personal information except to provide these services to
                the Company, and they are required to maintain the
                confidentiality of your information.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company may disclose your personal information, without
                notice, if required to do so by law or in the good faith belief
                that such action is necessary to: (a) conform to the edicts of
                the law or comply with legal process served on the Company or
                the site; (b) protect and defend the rights or property of the
                Company; and/or (c) act under exigent circumstances to protect
                the personal safety of users of the Company, or the public.
              </Text>
            </Box>

            {/* Opt-Out of Disclosure */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Opt-Out of Disclosure of Personal Information to Third Parties
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                In connection with any personal information we may disclose to a
                third party for a business purpose, you have the right to know:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  The categories of personal information that we disclosed about
                  you for a business purpose.
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You have the right under the CCPA and CPRA and certain other
                privacy and data protection laws, as applicable, to opt out of
                the disclosure of your personal information. If you exercise
                your right to opt out of the disclosure of your personal
                information, we will refrain from disclosing your personal
                information, unless you subsequently provide express
                authorization for the disclosure of your personal information.
                To opt out of the disclosure of your personal information,
                please contact us using the information provided below.
              </Text>
            </Box>

            {/* Right to Deletion */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Right to Deletion
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Subject to certain exceptions set out below, on receipt of a
                verifiable request from you, we will:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Delete your personal information from our records; and
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Direct any service providers to delete your personal
                  information from their records.
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Under the CCPA and CPRA, you have the right to request that the
                Company, and any third parties with whom your personal
                information is sold or shared, delete any personal information
                that has been collected about you. To exercise your rights,
                contact us at{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                />
                .
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                Please note that we may not be able to comply with requests to
                delete your personal information if it is necessary to:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Complete the transaction for which the personal information
                  was collected, fulfill the terms of a written warranty or
                  product recall conducted in accordance with federal law, and
                  provide a good or service requested by you, or reasonably
                  anticipated within the context of our ongoing business
                  relationship with you, or otherwise perform a contract between
                  you and us;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Detect security incidents, protect against malicious,
                  deceptive, fraudulent, or illegal activity; or prosecute those
                  responsible for that activity;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Debug to identify and repair errors that impair existing
                  intended functionality;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Exercise free speech, ensure the right of another consumer to
                  exercise his or her right of free speech, or exercise another
                  right provided for by law;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Comply with the California Electronic Communications Privacy
                  Act;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Engage in public or peer-reviewed scientific, historical, or
                  statistical research in the public interest that adheres to
                  all other applicable ethics and privacy laws, when our
                  deletion of the information is likely to render impossible or
                  seriously impair the achievement of such research, provided we
                  have obtained your informed consent;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Enable solely internal uses that are reasonably aligned with
                  your expectations based on your relationship with us;
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Comply with an existing legal obligation; or
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Otherwise use your personal information, internally, in a
                  lawful manner that is compatible with the context in which you
                  provided the information.
                </Text>
              </Box>
            </Box>

            {/* Children Under Thirteen */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Children Under Thirteen
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company does not knowingly collect personally identifiable
                information from children under the age of 13. If you are under
                the age of 13, you must ask your parent or guardian for
                permission to use this application.
              </Text>
            </Box>

            {/* Opt Out and Unsubscribe */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Opt Out and Unsubscribe from Third-Party Communications
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We respect your privacy and give you an opportunity to opt out
                of receiving announcements of certain information. Users may opt
                out of receiving any or all communications from third-party
                partners of the Company by contacting us here:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Email:{' '}
                  <SecureEmail
                    emailKey="support"
                    showAsLink={false}
                    component="span"
                  />
                </Text>
              </Box>
            </Box>

            {/* Email Communications */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Email Communications
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                From time to time, the Company may contact you via email for the
                purpose of providing announcements, promotional offers, alerts,
                confirmations, surveys, and/or other general communication. In
                order to improve our services, we may receive a notification
                when you open an email from the Company or click on a link
                therein.
              </Text>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you would like to stop receiving marketing or promotional
                communications via email from the Company, you may opt out of
                such communications by replying STOP or clicking on the
                unsubscribe button.
              </Text>
            </Box>

            {/* External Data Storage Sites */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                External Data Storage Sites
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We may store your data on servers provided by third-party
                hosting vendors with whom we have contracted.
              </Text>
            </Box>

            {/* Changes to This Statement */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Changes to This Statement
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company reserves the right to change this Policy from time
                to time. For example, when there are changes in our services,
                changes in our data protection practices, or changes in the law.
                When changes to this Policy are significant, we will inform you.
                You may receive a notice by sending an email to the primary
                email address specified in your account, by placing a prominent
                notice on our RSM Collective, and/or by updating any privacy
                information. Your continued use of the application and/or
                services available after such modifications will constitute
                your: (a) acknowledgment of the modified Policy; and (b)
                agreement to abide and be bound by that Policy.
              </Text>
            </Box>

            {/* Contact Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Contact Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                The Company welcomes your questions or comments regarding this
                Policy. If you believe that the Company has not adhered to this
                Policy, please contact the Company at:
              </Text>
              <Text sx={{ mb: 2, lineHeight: 1.7, fontWeight: 500 }}>
                RSM Collective
                <br />
                401 W A Street Ste. 200
                <br />
                San Diego, California 92101
                <br />
                <br />
                Email Address:
                <br />
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                />
                <br />
                <br />
                <br />
                Effective as of September 04, 2025
              </Text>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default PrivacyPolicy;
